// Profile-based spectral noise reduction (à la Audacity's Noise Reduction).
//
// 1. Sample a "noise profile" from audio that is *only* the unwanted noise —
//    either a live mic capture (user stays quiet) or a quiet stretch of a clip.
//    The profile is the average magnitude spectrum of that noise per FFT bin.
// 2. To denoise, STFT the signal and, per bin, subtract the noise estimate with
//    a spectral floor (so we attenuate, not null, → less "musical noise"), then
//    iSTFT. Gains are smoothed across frequency and time to avoid warbling.

const NF = 2048, HOP = 512, BINS = NF / 2 + 1, PAD = NF;
const WIN = (() => { const w = new Float32Array(NF); for (let i = 0; i < NF; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / NF); return w; })();

// In-place iterative radix-2 FFT (n a power of two). inverse scales by 1/n.
function fft(re: Float64Array, im: Float64Array, inverse: boolean): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { const tr = re[i]; re[i] = re[j]; re[j] = tr; const ti = im[i]; im[i] = im[j]; im[j] = ti; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const half = len >> 1;
    const ang = (2 * Math.PI / len) * (inverse ? 1 : -1);
    const wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let k = 0; k < half; k++) {
        const ar = re[i + k], ai = im[i + k];
        const br = re[i + k + half], bi = im[i + k + half];
        const vr = br * cr - bi * ci, vi = br * ci + bi * cr;
        re[i + k] = ar + vr; im[i + k] = ai + vi;
        re[i + k + half] = ar - vr; im[i + k + half] = ai - vi;
        const ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
      }
    }
  }
  if (inverse) for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
}

export interface NoiseProfile {
  mag: Float32Array;        // per-bin avg magnitude at NF (used by the offline per-clip path)
  samples: Float32Array;    // the raw mono noise (lets the live worklet build its own bins)
  sr: number; source: string; at: number;
}

let current: NoiseProfile | null = null;
let version = 0;
export function getNoiseProfile(): NoiseProfile | null { return current; }
export function getNoiseProfileVersion(): number { return version; }
export function setNoiseProfile(p: NoiseProfile | null): void { current = p; version++; }

// Per-insert live noise profiles, keyed by effect id. Each live Denoise insert
// carries its OWN profile, so track 1 and track 2 are independent (the global
// `current` above is only used by the offline per-clip bake). Runtime-only —
// same lifetime as the global profile, i.e. not serialized with the project.
const insertProfiles = new Map<string, NoiseProfile>();
const insertVersions = new Map<string, number>();
let insertVerSeq = 0;
export function getInsertProfile(id: string): NoiseProfile | null { return insertProfiles.get(id) ?? null; }
export function getInsertProfileVersion(id: string): number { return insertVersions.get(id) ?? 0; }
export function setInsertProfile(id: string, p: NoiseProfile | null): void {
  if (p) insertProfiles.set(id, p); else insertProfiles.delete(id);
  insertVersions.set(id, ++insertVerSeq);
}

/** True unless the signal is effectively digital silence (peak ≤ ~-100 dBFS) —
 *  guards against sampling a bogus profile from a stopped/empty source. */
export function hasAudioSignal(mono: Float32Array, threshold = 1e-5): boolean {
  let peak = 0;
  for (let i = 0; i < mono.length; i++) { const a = Math.abs(mono[i]); if (a > peak) peak = a; }
  return peak > threshold;
}

/** Average magnitude spectrum of a noise-only mono signal → noise profile. */
export function buildNoiseProfile(mono: Float32Array, sr: number, source: string): NoiseProfile {
  const sig = mono.length >= NF ? mono : (() => { const p = new Float32Array(NF); p.set(mono); return p; })();
  const numFrames = 1 + Math.floor((sig.length - NF) / HOP);
  const acc = new Float64Array(BINS);
  const re = new Float64Array(NF), im = new Float64Array(NF);
  for (let f = 0; f < numFrames; f++) {
    const s = f * HOP;
    for (let k = 0; k < NF; k++) { re[k] = sig[s + k] * WIN[k]; im[k] = 0; }
    fft(re, im, false);
    for (let k = 0; k < BINS; k++) acc[k] += Math.hypot(re[k], im[k]);
  }
  const mag = new Float32Array(BINS);
  for (let k = 0; k < BINS; k++) mag[k] = acc[k] / numFrames;
  return { mag, samples: mono.slice(), sr, source, at: Date.now() };
}

/** Profile-based denoise of one channel (offline, per-clip). strength in [0,1] sets
 *  the over-subtraction and the residual floor (-10..-30 dB); smoothing in [0,1]
 *  trades detail (low) for fewer "musical noise" artefacts (high).
 *  v1.1 calibration, shared with the live worklet and the Profile NR VST:
 *  decision-directed Wiener gain instead of magnitude subtraction + gain
 *  averaging, which measurably cut speech by 3-5 dB at the same noise reduction. */
export function denoiseChannel(samples: Float32Array, profile: Float32Array, strength = 0.7, smoothing = 0.5, sampleRate = 48000): Float32Array {
  const s = Math.min(1, Math.max(0, strength));
  const sm = Math.min(1, Math.max(0, smoothing));
  const beta = 1 + 1.5 * s;
  const floorGain = Math.pow(10, -(10 + 20 * s) / 20);
  const alphaRef = sm <= 0.5 ? 0.90 + 0.16 * sm : 0.98 + 0.02 * (sm - 0.5);
  const alpha = Math.pow(alphaRef, (HOP / sampleRate) / (512 / 48000)); // calibrated per 10.67 ms hop
  const medBlend = Math.min(1, Math.max(0, (sm - 0.5) * 2));
  const magToPow = 4 / Math.PI; // mean |N|^2 = (4/pi)(mean |N|)^2 for Rayleigh noise

  const n0 = samples.length;
  const sig = new Float32Array(n0 + 2 * PAD); sig.set(samples, PAD);
  const numFrames = 1 + Math.floor((Math.max(NF, sig.length) - NF) / HOP);
  const numSamples = NF + (numFrames - 1) * HOP;
  const out = new Float64Array(numSamples), denom = new Float64Array(numSamples);
  const re = new Float64Array(NF), im = new Float64Array(NF);
  const gain = new Float32Array(BINS);
  const cleanPow = new Float64Array(BINS);

  for (let f = 0; f < numFrames; f++) {
    const so = f * HOP;
    for (let k = 0; k < NF; k++) { re[k] = sig[so + k] * WIN[k]; im[k] = 0; }
    fft(re, im, false);
    for (let k = 0; k < BINS; k++) {
      const p = re[k] * re[k] + im[k] * im[k];
      const pn = beta * magToPow * profile[k] * profile[k] + 1e-20;
      const xi = Math.max(alpha * cleanPow[k] / pn + (1 - alpha) * Math.max(p / pn - 1, 0), 1e-4);
      const g = Math.min(1, Math.max(floorGain, xi / (1 + xi)));
      gain[k] = g; cleanPow[k] = g * g * p;
    }
    for (let k = 0; k < BINS; k++) {
      let g = gain[k];
      if (medBlend > 0 && k > 0 && k < BINS - 1) {
        const a = gain[k - 1], b = gain[k + 1];
        const med = Math.max(Math.min(a, b), Math.min(Math.max(a, b), g));
        g = (1 - medBlend) * g + medBlend * med;
      }
      re[k] *= g; im[k] *= g;
    }
    for (let k = 1; k < NF - BINS + 1; k++) { re[NF - k] = re[k]; im[NF - k] = -im[k]; }
    fft(re, im, true);
    for (let k = 0; k < NF; k++) { const wv = WIN[k]; out[so + k] += re[k] * wv; denom[so + k] += wv * wv; }
  }
  const result = new Float32Array(n0);
  for (let i = 0; i < n0; i++) { const j = i + PAD; result[i] = j < numSamples && denom[j] ? out[j] / denom[j] : 0; }
  return result;
}

/** Capture a short noise-only mono sample from the live mic (user stays quiet). */
export async function captureNoiseFromMic(
  ctx: AudioContext,
  opts: { deviceId?: string | null; seconds?: number } = {},
): Promise<Float32Array> {
  const seconds = opts.seconds ?? 1.5;
  await ctx.audioWorklet.addModule("/recorder-worklet.js").catch(() => {});
  const audio: MediaTrackConstraints = { echoCancellation: false, noiseSuppression: false, autoGainControl: false };
  if (opts.deviceId) audio.deviceId = { exact: opts.deviceId };
  const stream = await navigator.mediaDevices.getUserMedia({ audio });
  const src = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, "recorder-processor");
  const sink = ctx.createGain(); sink.gain.value = 0; // capture only, no monitor
  src.connect(node); node.connect(sink); sink.connect(ctx.destination);

  const blocks: Float32Array[] = [];
  node.port.onmessage = (e) => { if (e.data?.type === "data") blocks.push(e.data.chans[0]); };
  const cleanup = () => {
    try { node.port.postMessage("stop"); } catch {}
    try { src.disconnect(); node.disconnect(); sink.disconnect(); } catch {}
    stream.getTracks().forEach((t) => t.stop());
  };
  try {
    node.port.postMessage("start");
    await new Promise((r) => setTimeout(r, seconds * 1000));
    node.port.postMessage("stop");
    await new Promise((r) => setTimeout(r, 150)); // flush trailing data
  } finally { cleanup(); }

  const total = blocks.reduce((n, b) => n + b.length, 0);
  if (!total) throw new Error("No audio captured from the mic.");
  const data = new Float32Array(total);
  let o = 0; for (const b of blocks) { data.set(b, o); o += b.length; }
  return data;
}
